import React, { useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { getServiceCategoryIcon } from '../../config/serviceCategoryIcons';
import { servicesService } from '../../services/servicesService';
import html2canvas from 'html2canvas';
import './ServiceCard.css';

// Initialize Supabase client
const SUPABASE_URL = 'https://dlmcxxzohervnbixauhs.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImRsbWN4eHpvaGVydm5iaXhhdWhzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDUxNzcwNzEsImV4cCI6MjA2MDc1MzA3MX0.UIQ7Wqo3R9aTYHYQHTFsHWGdhFFKLHjhEShlz2J2J-Q';

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
  auth: {
    storage: window.localStorage,
    storageKey: 'community_services_auth',
  }
});

/**
 * ServiceCard Component - Individual service display card
 */
function ServiceCard({
  service,
  type,
  onOfferHelp,
  isAuthenticated,
  isOwn,
  onUpdate
}) {
  const [showDetails, setShowDetails] = useState(false);
  const [contacting, setContacting] = useState(false);
  const [contactOptions, setContactOptions] = useState(null);
  const [showFallbackModal, setShowFallbackModal] = useState(false);
  const [fallbackPhone, setFallbackPhone] = useState(null);
  
  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.floor((date - now) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Tomorrow';
    if (diffDays < 7) return `${diffDays} days`;
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  // Get urgency styling
  const getUrgencyClass = () => {
    if (type !== 'needed') return '';
    return `urgency-${service.urgency || 'normal'}`;
  };

  // Get member display name
  const getMemberDisplay = () => {
    // Check if this is an admin-created service with proxy contact info
    if (service.proxy_contact_name) {
      return `${service.proxy_contact_name}`;
    }

    const member = service.member;
    if (!member) return 'Community Member';

    let display = `${member.first_name} ${member.last_initial || ''}`.trim();

    // Add verification badge if member is trusted
    if (member.services_completed >= 5) {
      display += ' ✓';
    }

    return display;
  };

  // Get contact button text
  const getContactButtonText = () => {
    if (contacting) return 'Opening WhatsApp...';
    if (type === 'needed') {
      return 'Offer Help';
    }
    return service.allow_whatsapp ? 'Contact via WhatsApp' : 'Get Help';
  };

  // Handle contact via WhatsApp/Phone/SMS
  const handleContact = async () => {
    setContacting(true);

    try {
      const { data, error } = await supabase.functions.invoke('generate-whatsapp-url', {
        body: {
          service_id: service.id
        }
      });

      if (error) {
        console.error('Error generating contact URL:', error);
        alert('Failed to generate contact link. Please try again.');
        setContacting(false);
        return;
      }

      if (!data || !data.success) {
        alert(data?.error || 'Failed to generate contact link. Please try again.');
        setContacting(false);
        return;
      }

      // SCENARIO 1: Single contact URL (WhatsApp or single method)
      if (data.contact_url) {
        // Platform detection
        const userAgent = navigator.userAgent;
        const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
        const isAndroid = /Android/i.test(userAgent);
        const isMobile = isIOS || isAndroid;

        if (isMobile) {
          // Extract phone and message for platform-specific URLs
          const waUrl = data.contact_url; // https://wa.me/...
          const phone = data.phone_number || waUrl.match(/wa\.me\/(\d+)/)?.[1] || '';
          const message = waUrl.match(/text=([^&]*)/)?.[1] || '';

          let deepLinkUrl;

          if (isIOS) {
            // iOS: Use whatsapp:// scheme (direct to app, no browser navigation)
            deepLinkUrl = `whatsapp://send?phone=${phone}&text=${message}`;
          } else if (isAndroid) {
            // Android: Use intent:// with fallback to wa.me if app not installed
            const fallbackUrl = encodeURIComponent(waUrl);
            deepLinkUrl = `intent://send/?phone=${phone}&text=${message}#Intent;scheme=whatsapp;package=com.whatsapp;S.browser_fallback_url=${fallbackUrl};end;`;
          }

          // Launch WhatsApp using platform-specific deep link
          window.location.href = deepLinkUrl;

          // Smart fallback: Show helper modal after 3s if WhatsApp didn't open
          let helperId = setTimeout(() => {
            if (data.phone_number) {
              setFallbackPhone(data.phone_number);
              setShowFallbackModal(true);
            }
          }, 3000);

          // Cancel helper if visibility changes (Android Chrome fires this reliably)
          const cancelHelper = () => {
            clearTimeout(helperId);
            cleanup();
          };

          const cleanup = () => {
            document.removeEventListener('visibilitychange', cancelHelper);
            document.removeEventListener('pagehide', cancelHelper);
          };

          document.addEventListener('visibilitychange', cancelHelper, { once: true });
          document.addEventListener('pagehide', cancelHelper, { once: true });

        } else {
          // Desktop: Open WhatsApp Web in new tab (keeps site open)
          window.open(data.contact_url, '_blank', 'noopener,noreferrer');
        }

        // Log analytics with category for popularity tracking (anonymous inserts allowed via RLS)
        servicesService.logContactAnalytics(
          service.id,
          data.contact_method || 'whatsapp',
          service.category,
          service.center_id
        ).catch(err => console.error('Failed to log analytics:', err));

        setContacting(false);
      }
      // SCENARIO 2: Multiple options (call/text)
      else if (data.has_multiple_options && data.options) {
        // Show modal for user to choose
        setContactOptions(data);
        setContacting(false);
      }
      else {
        alert('No contact methods available for this service.');
        setContacting(false);
      }
    } catch (err) {
      console.error('Error in handleContact:', err);
      alert('An error occurred. Please try again.');
      setContacting(false);
    }
  };

  // Handle selection from contact options modal
  const handleContactOptionSelect = (option) => {
    // Platform detection
    const userAgent = navigator.userAgent;
    const isIOS = /iPhone|iPad|iPod/i.test(userAgent);
    const isAndroid = /Android/i.test(userAgent);
    const isMobile = isIOS || isAndroid;

    if (isMobile) {
      // If WhatsApp option, use platform-specific deep links
      if (option.method === 'whatsapp') {
        const waUrl = option.url; // https://wa.me/...
        const phone = contactOptions.phone_number || waUrl.match(/wa\.me\/(\d+)/)?.[1] || '';
        const message = waUrl.match(/text=([^&]*)/)?.[1] || '';

        let deepLinkUrl;

        if (isIOS) {
          deepLinkUrl = `whatsapp://send?phone=${phone}&text=${message}`;
        } else if (isAndroid) {
          const fallbackUrl = encodeURIComponent(waUrl);
          deepLinkUrl = `intent://send/?phone=${phone}&text=${message}#Intent;scheme=whatsapp;package=com.whatsapp;S.browser_fallback_url=${fallbackUrl};end;`;
        }

        window.location.href = deepLinkUrl;

        // Smart fallback timer
        let helperId = setTimeout(() => {
          if (contactOptions.phone_number) {
            setFallbackPhone(contactOptions.phone_number);
            setShowFallbackModal(true);
          }
        }, 3000);

        const cancelHelper = () => {
          clearTimeout(helperId);
          cleanup();
        };

        const cleanup = () => {
          document.removeEventListener('visibilitychange', cancelHelper);
          document.removeEventListener('pagehide', cancelHelper);
        };

        document.addEventListener('visibilitychange', cancelHelper, { once: true });
        document.addEventListener('pagehide', cancelHelper, { once: true });

      } else {
        // For call/SMS, open directly (these don't navigate away)
        window.location.href = option.url;
      }
    } else {
      // Desktop: Open in new tab
      window.open(option.url, '_blank', 'noopener,noreferrer');
    }

    // Log analytics (anonymous inserts allowed via RLS)
    servicesService.logContactAnalytics(
      service.id,
      option.method,
      service.category,
      service.center_id
    ).catch(err => console.error('Failed to log analytics:', err));

    // Close modal
    setContactOptions(null);
  };

  // Handle mark as fulfilled
  const handleMarkFulfilled = async () => {
    if (!window.confirm('Mark this service as fulfilled?')) return;

    try {
      const { data, error } = await supabase.functions.invoke('update-service', {
        body: {
          service_id: service.id,
          updates: {
            status: 'fulfilled'
          }
        }
      });

      if (error) {
        console.error('Error updating service:', error);
        alert('Failed to update service. Please try again.');
        return;
      }

      if (data && data.success && onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error in handleMarkFulfilled:', err);
      alert('An error occurred. Please try again.');
    }
  };

  // Handle remove service
  const handleRemove = async () => {
    if (!window.confirm('Remove this service? This cannot be undone.')) return;

    try {
      const { data, error } = await supabase.functions.invoke('update-service', {
        body: {
          service_id: service.id,
          updates: {
            status: 'removed'
          }
        }
      });

      if (error) {
        console.error('Error removing service:', error);
        alert('Failed to remove service. Please try again.');
        return;
      }

      if (data && data.success && onUpdate) {
        onUpdate();
      }
    } catch (err) {
      console.error('Error in handleRemove:', err);
      alert('An error occurred. Please try again.');
    }
  };

  // Handle share service
  const handleShare = async () => {
    const cardRef = document.getElementById(`service-card-${service.id}`);

    if (!cardRef) {
      console.error('Service card element not found');
      return;
    }

    const shareUrl = `${window.location.origin}/community/services?id=${service.id}&type=${type}`;
    const shareTitle = `*${service.title}*`;

    // Format member display name - use getMemberDisplay() to handle proxy names
    const memberName = getMemberDisplay().replace(' ✓', ''); // Remove checkmark for share text

    // Build share message based on service type
    let shareMessage = '';
    if (type === 'offered') {
      shareMessage = `${shareTitle}\n\n${memberName} is offering: ${service.description}\n\n📍 Category: ${service.category}`;

      if (service.member?.services_completed >= 5) {
        shareMessage += `\n✓ Trusted helper (${service.member.services_completed} services completed)`;
      }

      shareMessage += '\n\n👉 Contact or view details:';
    } else {
      shareMessage = `${shareTitle}\n\n${memberName} needs help: ${service.description}\n\n📍 Category: ${service.category}`;

      if (service.urgency && service.urgency !== 'normal') {
        const urgencyEmoji = service.urgency === 'emergency' ? '🚨' : '⚠️';
        shareMessage += `\n${urgencyEmoji} Urgency: ${service.urgency}`;
      }

      if (service.needed_by) {
        shareMessage += `\n⏰ Needed by: ${formatDate(service.needed_by)}`;
      }

      if (service.service_city || service.service_state) {
        shareMessage += `\n📍 Location: ${service.service_city}${service.service_city && service.service_state ? ', ' : ''}${service.service_state || ''}`;
        if (service.location_note) {
          shareMessage += ` • ${service.location_note}`;
        }
      }

      shareMessage += '\n\n👉 Offer help or view details:';
    }

    try {
      // Hide share button temporarily during screenshot
      const shareButton = cardRef.querySelector('.share-icon-indicator');
      if (shareButton) {
        shareButton.style.display = 'none';
      }

      // Capture the service card as canvas
      const canvas = await html2canvas(cardRef, {
        backgroundColor: '#ffffff',
        scale: 2,
        logging: false,
        useCORS: true,
        allowTaint: true
      });

      // Show share button again
      if (shareButton) {
        shareButton.style.display = 'flex';
      }

      // Convert canvas to blob
      const blob = await new Promise(resolve =>
        canvas.toBlob(resolve, 'image/png')
      );

      // Create file from blob
      const fileName = `service-${type}-${service.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}.png`;
      const file = blob ? new File([blob], fileName, { type: 'image/png' }) : null;

      // Share data
      const shareData = {
        title: service.title,
        text: shareMessage,
        url: shareUrl
      };

      // Try to share with image on mobile
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          ...shareData,
          files: [file]
        });
      } else if (navigator.share) {
        // Share without image
        await navigator.share(shareData);
      } else if (file) {
        // Desktop: download image and copy link
        const url = URL.createObjectURL(file);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        await navigator.clipboard.writeText(`${shareMessage}\n${shareUrl}`);
        alert('Service card image downloaded and link copied to clipboard!');
      } else {
        // Just copy link
        await navigator.clipboard.writeText(`${shareMessage}\n${shareUrl}`);
        alert('Service link copied to clipboard!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing service:', error);
        // Fallback to copy link
        try {
          await navigator.clipboard.writeText(`${shareMessage}\n${shareUrl}`);
          alert('Link copied to clipboard!');
        } catch {
          alert('Failed to share. Please try again.');
        }
      }
    }
  };

  const categoryConfig = getServiceCategoryIcon(service.category);
  const IconComponent = categoryConfig.icon;

  // Determine primary contact method icon
  const getContactIcon = () => {
    // Show contact icon for BOTH offered and needed services (if not owner)
    // Owner check is handled by !isOwn condition in rendering (line 461)
    if (service.allow_whatsapp) return { icon: '📱', color: '#25D366', label: 'WhatsApp' };
    if (service.allow_calls) return { icon: '📞', color: '#4285F4', label: 'Call' };
    if (service.allow_sms) return { icon: '💬', color: '#FF9800', label: 'SMS' };
    return null;
  };

  const contactIcon = getContactIcon();

  return (
    <div
      id={`service-card-${service.id}`}
      className={`service-card-horizontal ${getUrgencyClass()} ${isOwn ? 'own-service' : ''} ${showDetails ? 'expanded' : 'collapsed'}`}
      data-service-id={service.id}
    >
      {/* Main Horizontal Row - Always Visible */}
      <div className="horizontal-row" onClick={() => setShowDetails(!showDetails)}>
        {/* 1. Category Icon */}
        <div className="category-icon-circle">
          <IconComponent
            style={{
              fontSize: '20px',
              color: categoryConfig.color
            }}
          />
        </div>

        {/* 2. Title + Name + Description Column */}
        <div className="content-column">
          <div className="title-row">
            <h3 className="row-title">
              {service.title}
              {type === 'needed' && service.urgency !== 'normal' && (
                <span className={`urgency-inline ${service.urgency}`}>
                  {service.urgency === 'emergency' ? '🚨' : '⚠️'}
                </span>
              )}
            </h3>
            <div className="row-name">{getMemberDisplay()}</div>
          </div>
          <p className="row-description">
            {service.description}
          </p>
          {/* Location badge for Services Needed (collapsed view) */}
          {type === 'needed' && (service.service_city || service.service_state) && (
            <div style={{
              fontSize: '11px',
              color: '#666',
              marginTop: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}>
              📍 {service.service_city}{service.service_city && service.service_state ? ', ' : ''}{service.service_state || ''}
            </div>
          )}
        </div>

        {/* 3. Volunteer Badge (circular V) */}
        {service.is_volunteer && (
          <div className="volunteer-badge-circle" title="Volunteer Service">
            V
          </div>
        )}

        {/* 3.5 Share Icon - Always visible for everyone */}
        <div
          className="share-icon-indicator"
          title="Share this service"
          onClick={(e) => {
            e.stopPropagation();
            handleShare();
          }}
        >
          <span style={{ fontSize: '18px' }}>🔗</span>
        </div>

        {/* 4. Contact Icon (for offered services) */}
        {contactIcon && !isOwn && (
          <div
            className="contact-icon-indicator"
            title={contactIcon.label}
            onClick={(e) => {
              e.stopPropagation();
              handleContact();
            }}
          >
            <span style={{ fontSize: '20px' }}>{contactIcon.icon}</span>
          </div>
        )}

        {/* Owner Action Icons (for own services) */}
        {isOwn && (
          <div className="owner-quick-actions">
            {type === 'needed' && (
              <button
                className="mini-icon-btn success"
                onClick={(e) => {
                  e.stopPropagation();
                  handleMarkFulfilled();
                }}
                title="Mark as Fulfilled"
              >
                ✓
              </button>
            )}
            <button
              className="mini-icon-btn danger"
              onClick={(e) => {
                e.stopPropagation();
                handleRemove();
              }}
              title="Remove"
            >
              🗑
            </button>
          </div>
        )}

        {/* 5. Expand Arrow */}
        <div className="expand-arrow">
          <span className={`arrow-icon ${showDetails ? 'rotated' : ''}`}>→</span>
        </div>
      </div>

      {/* Expanded Details - Shown when clicked */}
      {showDetails && (
        <div className="expanded-details">
          {/* Full Description */}
          <div className="expanded-description-section">
            <p className="full-description">{service.description}</p>
          </div>

          {/* Additional Info Grid */}
          <div className="expanded-info">
            {/* Category */}
            <div className="info-row">
              <span className="info-label">Category:</span>
              <span className="info-text">{service.category}</span>
            </div>

            {/* Member Stats */}
            {service.member?.services_completed > 0 && (
              <div className="info-row">
                <span className="info-label">Helped:</span>
                <span className="info-text">{service.member.services_completed} times</span>
              </div>
            )}

            {/* Contact Preferences */}
            {type === 'offered' && (
              <div className="info-row">
                <span className="info-label">Contact via:</span>
                <div className="contact-chips">
                  {service.allow_whatsapp && <span className="chip whatsapp">WhatsApp</span>}
                  {service.allow_calls && <span className="chip calls">Calls</span>}
                  {service.allow_sms && <span className="chip sms">SMS</span>}
                </div>
              </div>
            )}

            {/* Helpers Count */}
            {type === 'needed' && service.helpers_found > 0 && (
              <div className="info-row">
                <span className="info-label">Helpers:</span>
                <span className="info-text">{service.helpers_found} offered</span>
              </div>
            )}

            {/* Needed By Date */}
            {type === 'needed' && service.needed_by && (
              <div className="info-row">
                <span className="info-label">Needed by:</span>
                <span className="info-text">{formatDate(service.needed_by)}</span>
              </div>
            )}

            {/* Location */}
            {type === 'needed' && (service.service_city || service.service_state) && (
              <div className="info-row">
                <span className="info-label">📍 Location:</span>
                <span className="info-text">
                  {service.service_city}{service.service_city && service.service_state && ', '}{service.service_state}
                  {service.location_note && ` • ${service.location_note}`}
                </span>
              </div>
            )}

            {/* Volunteer Service Badge */}
            {service.is_volunteer && (
              <div className="info-row volunteer-highlight">
                <span className="info-label">Service Type:</span>
                <div className="volunteer-badge-full">
                  <span className="volunteer-icon">V</span>
                  <span className="volunteer-text">Volunteer Service</span>
                </div>
              </div>
            )}
          </div>

          {/* Volunteer Message */}
          {service.is_volunteer && (
            <div className="volunteer-message">
              ℹ️ This is a volunteer service offered free of charge to help our community.
            </div>
          )}

          {/* Actions */}
          <div className="expanded-actions">
            {!isOwn && (
              <button
                className="expanded-contact-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleContact();
                }}
                disabled={contacting}
              >
                {type === 'needed' ? 'Offer to Help' : 'Contact'}
              </button>
            )}
            <div
              className="share-icon-minimal"
              onClick={(e) => {
                e.stopPropagation();
                handleShare();
              }}
              title="Share this service"
            >
              🔗
            </div>
          </div>
        </div>
      )}

      {/* Contact Options Modal - Shown when multiple contact methods available */}
      {contactOptions && (
        <div
          className="contact-modal-overlay"
          onClick={() => setContactOptions(null)}
        >
          <div
            className="contact-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Contact {getMemberDisplay()}</h3>
            <p className="contact-modal-subtitle">Choose a contact method:</p>
            <div className="contact-options-buttons">
              {contactOptions.options.map((option) => (
                <button
                  key={option.method}
                  className={`contact-option-btn ${option.method}`}
                  onClick={() => handleContactOptionSelect(option)}
                >
                  <span className="contact-icon">
                    {option.method === 'call' ? '📞' : '💬'}
                  </span>
                  <span className="contact-label">{option.label}</span>
                </button>
              ))}
            </div>
            <button
              className="contact-modal-close"
              onClick={() => setContactOptions(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Helper Modal - Shown if WhatsApp didn't open (non-error, user-friendly) */}
      {showFallbackModal && (
        <div
          className="contact-modal-overlay"
          onClick={() => setShowFallbackModal(false)}
        >
          <div
            className="contact-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>Need Another Way to Reach Them?</h3>
            <p className="contact-modal-subtitle">If WhatsApp didn't open, try these options:</p>
            <div className="contact-options-buttons">
              <button
                className="contact-option-btn call"
                onClick={() => {
                  window.location.href = `tel:${fallbackPhone}`;
                  servicesService.logContactAnalytics(
                    service.id,
                    'phone_call',
                    service.category,
                    service.center_id
                  ).catch(err => console.error('Failed to log analytics:', err));
                  setShowFallbackModal(false);
                }}
              >
                <span className="contact-icon">📞</span>
                <span className="contact-label">Call {fallbackPhone}</span>
              </button>
              <button
                className="contact-option-btn sms"
                onClick={() => {
                  window.location.href = `sms:${fallbackPhone}`;
                  servicesService.logContactAnalytics(
                    service.id,
                    'sms',
                    service.category,
                    service.center_id
                  ).catch(err => console.error('Failed to log analytics:', err));
                  setShowFallbackModal(false);
                }}
              >
                <span className="contact-icon">💬</span>
                <span className="contact-label">Send SMS to {fallbackPhone}</span>
              </button>
            </div>
            <button
              className="contact-modal-close"
              onClick={() => setShowFallbackModal(false)}
            >
              It Worked, Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ServiceCard;